import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/Toast";
import { Band, BackBar, Page } from "../components/Band";
import Modal, { ModalBody, ModalFooter } from "../components/Modal";
import { Button, Card, Content, Empty, Pill, SkeletonRows } from "../components/primitives";
import RequestCard from "./superAdmin/RequestCard";
import useClubRequests from "./superAdmin/useClubRequests";

// Names for the confirm dialog; clubData is applicant-written, so read defensively (RequestCard.jsx).
const asText = (v) => (typeof v === "string" ? v.trim() : "");
function requestNames(r) {
  const c = r.clubData && typeof r.clubData === "object" ? r.clubData : {};
  return { club: asText(c.name) || "this club request", who: asText(r.user?.full_name) };
}

/**
 * /super: mirrors screens/SuperAdminScreen.js. Only "New club requests" exist
 * (leader-claim requests are gone; the manual push has no client caller in the app).
 */
export default function SuperAdminPage() {
  const { user, isSuperAdmin } = useAuth();
  const toast = useToast();
  const { requests, loading, error, reload, pending, review } = useClubRequests({
    enabled: isSuperAdmin, uid: user?.uid, toast,
  });
  const [confirm, setConfirm] = useState(null); // { id, action, club, who }

  if (!isSuperAdmin) {
    return (
      <Page>
        <BackBar title="Super Admin" testId="superAdmin" />
        <Content><Card className="empty" pad>Only super admins can open this page.</Card></Content>
      </Page>
    );
  }

  const decide = () => {
    const c = confirm;
    setConfirm(null);
    if (c) review(c.id, c.action);
  };

  return (
    <Page>
      <Band title="Super Admin" sub="App-wide moderation and requests" />
      <Content>
        {/* .content overlaps the band by 30px: start the heading below the band, on the page background. */}
        <div className="section-h" style={{ marginTop: 44 }}>
          <h2>New club requests</h2>
          <Pill tone="amber">{requests.length}</Pill>
        </div>
        {loading ? <SkeletonRows rows={3} />
          : error ? (
            <Empty icon="alert" title="Couldn't load requests">
              <p>{error}</p>
              <Button variant="sec" size="sm" icon="refresh" onClick={reload} data-testid="superAdmin.retry" style={{ marginTop: 12 }}>Retry</Button>
            </Empty>
          ) : requests.length === 0 ? (
            <Empty title="No pending requests">New club requests show up here.</Empty>
          ) : (
            <div className="grid g2" style={{ alignItems: "start" }}>
              {requests.map((r) => (
                <RequestCard
                  key={r.id}
                  req={r}
                  pendingAction={pending[r.id]}
                  onApprove={() => setConfirm({ id: r.id, action: "approve", ...requestNames(r) })}
                  onReject={() => setConfirm({ id: r.id, action: "reject", ...requestNames(r) })}
                />
              ))}
            </div>
          )}
      </Content>
      {confirm && (
        <Modal title={confirm.action === "approve" ? "Approve request" : "Reject request"} size="narrow" onClose={() => setConfirm(null)} testId={`superAdmin.${confirm.action}Confirm`}>
          <ModalBody>
            <p>
              {confirm.action === "approve" ? "Approve " : "Reject "}
              <b>{confirm.club}</b>{confirm.who ? <> from <b>{confirm.who}</b></> : null}?{" "}
              {confirm.action === "approve" ? "The club will be created and the requester will become its admin." : null}
            </p>
          </ModalBody>
          <ModalFooter>
            {/* Focus starts on Cancel: both decisions are irreversible. */}
            <Button variant="ghost" onClick={() => setConfirm(null)} data-autofocus="" data-testid={`superAdmin.${confirm.action}Confirm.cancel`}>Cancel</Button>
            <Button variant={confirm.action === "approve" ? "ok" : "dangerSolid"} onClick={decide} data-testid={`superAdmin.${confirm.action}Confirm.confirm`}>
              {confirm.action === "approve" ? "Approve" : "Reject"}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Page>
  );
}
